/**
 * Case pipeline — switchable Kanban board and advanced data table. Server-side keyset
 * pagination, search + multi-facet filters, sort, CSV export, row quick-preview drawer.
 * Loading / empty / error states throughout.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus, Search, LayoutGrid, Table2, Download, RefreshCw, AlertTriangle, ChevronRight,
} from 'lucide-react';
import { api } from '../../lib/apiClient.js';
import { useToast } from '../../components/feedback/ToastProvider.jsx';
import { StatusBadge } from '../../components/ui/StatusBadge.jsx';
import {
  PIPELINE, STATUS_META, CATEGORY_META, formatINR, relativeTime,
} from '../../features/cases/caseMeta.js';
import styles from './CasePipeline.module.css';

const CATEGORIES = ['housing', 'ration', 'medical', 'other'];

export default function CasePipeline() {
  const toast = useToast();
  const navigate = useNavigate();
  const [view, setView] = useState(() => localStorage.getItem('usmehar.cases.view') || 'table');
  const [filters, setFilters] = useState({ q: '', status: '', category: '', flagged: false });
  const [sort, setSort] = useState({ key: 'createdAt', dir: 'desc' });
  const [state, setState] = useState({ status: 'loading', cases: [], nextCursor: null, hasMore: false });
  const nextCursorRef = useRef(null);

  useEffect(() => localStorage.setItem('usmehar.cases.view', view), [view]);

  const buildParams = useCallback(
    (cursor) => {
      const p = new URLSearchParams({ limit: view === 'kanban' ? '100' : '25' });
      if (filters.q) p.set('q', filters.q);
      if (filters.status) p.set('status', filters.status);
      if (filters.category) p.set('category', filters.category);
      if (filters.flagged) p.set('flagged', 'true');
      if (cursor) p.set('cursor', cursor);
      return p;
    },
    [filters, view],
  );

  const load = useCallback(
    async (append = false) => {
      // Read the live cursor from a ref — a stale closure over state.nextCursor would
      // make "Load more" re-fetch the first page and append duplicates.
      const cursor = append ? nextCursorRef.current : null;
      setState((s) => ({ ...s, status: append ? s.status : 'loading' }));
      try {
        const env = await api.getEnvelope(`/cases?${buildParams(cursor)}`);
        nextCursorRef.current = env.meta.page.nextCursor;
        setState((s) => ({
          status: 'ready',
          cases: append ? [...s.cases, ...env.data] : env.data,
          nextCursor: env.meta.page.nextCursor,
          hasMore: env.meta.page.hasMore,
        }));
      } catch (err) {
        setState({ status: 'error', cases: [], nextCursor: null, hasMore: false, error: err.message });
      }
    },
    [buildParams],
  );

  useEffect(() => {
    const t = setTimeout(() => load(false), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, view]);

  const sorted = useMemo(() => {
    const arr = [...state.cases];
    const { key, dir } = sort;
    arr.sort((a, b) => {
      let av = a[key];
      let bv = b[key];
      if (key === 'beneficiary') {
        av = a.beneficiarySummary?.name || '';
        bv = b.beneficiarySummary?.name || '';
      }
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [state.cases, sort]);

  const exportCsv = () => {
    const header = 'caseNo,beneficiary,village,category,flow,amountRequested,status,created\n';
    const rows = sorted
      .map((c) =>
        [
          c.caseNo,
          c.beneficiarySummary?.name || '',
          c.beneficiarySummary?.village || '',
          c.category,
          c.flow,
          c.amountRequested,
          c.status,
          new Date(c.createdAt).toISOString(),
        ]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(','),
      )
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'cases.csv';
    a.click();
    toast.success(`Exported ${sorted.length} cases.`);
  };

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="eyebrow">Casework</p>
          <h1 className={styles.h1}>Case pipeline</h1>
        </div>
        <Link to="/cases/intake" className={styles.primary}>
          <Plus size={17} /> New intake
        </Link>
      </header>

      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <Search size={16} />
          <input
            placeholder="Search case no, summary…"
            value={filters.q}
            onChange={(e) => setFilter('q', e.target.value)}
          />
        </div>
        <select value={filters.status} onChange={(e) => setFilter('status', e.target.value)} className={styles.select}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_META).map(([k, m]) => (
            <option key={k} value={k}>
              {m.label}
            </option>
          ))}
        </select>
        <select value={filters.category} onChange={(e) => setFilter('category', e.target.value)} className={styles.select}>
          <option value="">All categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {CATEGORY_META[c].label}
            </option>
          ))}
        </select>
        <button
          className={`${styles.chip} ${filters.flagged ? styles.chipOn : ''}`}
          onClick={() => setFilter('flagged', !filters.flagged)}
          title="Show only duplicate-flagged cases"
        >
          <AlertTriangle size={14} /> Flagged
        </button>
        <div className={styles.spacer} />
        <div className={styles.viewToggle}>
          <button className={view === 'table' ? styles.viewActive : ''} onClick={() => setView('table')} title="Table view">
            <Table2 size={16} />
          </button>
          <button className={view === 'kanban' ? styles.viewActive : ''} onClick={() => setView('kanban')} title="Board view">
            <LayoutGrid size={16} />
          </button>
        </div>
        <button className={styles.ghost} onClick={exportCsv} title="Export CSV">
          <Download size={15} />
        </button>
        <button className={styles.ghost} onClick={() => load(false)} title="Refresh">
          <RefreshCw size={15} />
        </button>
      </div>

      {state.status === 'error' && (
        <div className={styles.error}>
          {state.error} — <button onClick={() => load(false)}>retry</button>
        </div>
      )}

      {view === 'table' ? (
        <CaseTable
          status={state.status}
          cases={sorted}
          sort={sort}
          onSort={(key) =>
            setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))
          }
          onOpen={(id) => navigate(`/cases/${id}`)}
          hasMore={state.hasMore}
          onLoadMore={() => load(true)}
        />
      ) : (
        <KanbanBoard status={state.status} cases={state.cases} onOpen={(id) => navigate(`/cases/${id}`)} />
      )}
    </div>
  );
}

/* ── Data table ──────────────────────────────────────────────────────────── */
function CaseTable({ status, cases, sort, onSort, onOpen, hasMore, onLoadMore }) {
  const cols = [
    { key: 'caseNo', label: 'Case' },
    { key: 'beneficiary', label: 'Beneficiary' },
    { key: 'category', label: 'Category' },
    { key: 'amountRequested', label: 'Amount', align: 'right' },
    { key: 'status', label: 'Status' },
    { key: 'createdAt', label: 'Created' },
  ];

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {cols.map((c) => (
              <th
                key={c.key}
                onClick={() => onSort(c.key)}
                className={`${styles.sortable} ${c.align === 'right' ? styles.right : ''}`}
              >
                {c.label}
                {sort.key === c.key && <span className={styles.sortArrow}>{sort.dir === 'asc' ? '▲' : '▼'}</span>}
              </th>
            ))}
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {status === 'loading' &&
            Array.from({ length: 8 }).map((_, i) => (
              <tr key={i}>
                <td colSpan={7}>
                  <div className={styles.skeleton} />
                </td>
              </tr>
            ))}
          {status === 'ready' && cases.length === 0 && (
            <tr>
              <td colSpan={7} className={styles.empty}>
                No cases match these filters.
              </td>
            </tr>
          )}
          {status === 'ready' &&
            cases.map((c) => {
              const Cat = CATEGORY_META[c.category]?.icon;
              return (
                <tr key={c.id} onClick={() => onOpen(c.id)} className={styles.row}>
                  <td>
                    <strong className="mono">{c.caseNo}</strong>
                    {c.dupFlags?.length > 0 && (
                      <span className={styles.flag} title="Duplicate flag">
                        <AlertTriangle size={12} />
                      </span>
                    )}
                  </td>
                  <td>
                    {c.beneficiarySummary?.name || '—'}
                    <span className={styles.sub}>{c.beneficiarySummary?.village}</span>
                  </td>
                  <td>
                    <span className={styles.cat}>
                      {Cat && <Cat size={15} />} {CATEGORY_META[c.category]?.label}
                    </span>
                  </td>
                  <td className={`${styles.right} mono`}>{formatINR(c.amountRequested)}</td>
                  <td>
                    <StatusBadge status={c.status} />
                  </td>
                  <td className={styles.sub}>{relativeTime(c.createdAt)}</td>
                  <td>
                    <button
                      className={styles.openBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpen(c.id);
                      }}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
      {hasMore && status === 'ready' && (
        <button className={styles.loadMore} onClick={onLoadMore}>
          Load more
        </button>
      )}
    </div>
  );
}

/* ── Kanban board ────────────────────────────────────────────────────────── */
function KanbanBoard({ status, cases, onOpen }) {
  const grouped = useMemo(() => {
    const g = Object.fromEntries(PIPELINE.map((s) => [s, []]));
    for (const c of cases) if (g[c.status]) g[c.status].push(c);
    return g;
  }, [cases]);

  if (status === 'loading') {
    return (
      <div className={styles.board}>
        {PIPELINE.map((s) => (
          <div key={s} className={styles.column}>
            <div className={styles.colHead}>{STATUS_META[s].label}</div>
            <div className={styles.skeletonCard} />
            <div className={styles.skeletonCard} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.board}>
      {PIPELINE.map((s) => (
        <div key={s} className={styles.column}>
          <div className={styles.colHead}>
            <StatusBadge status={s} />
            <span className={styles.colCount}>{grouped[s].length}</span>
          </div>
          <div className={styles.colBody}>
            {grouped[s].length === 0 && <p className={styles.colEmpty}>—</p>}
            {grouped[s].map((c) => {
              const Cat = CATEGORY_META[c.category]?.icon;
              return (
                <button key={c.id} className={styles.card} onClick={() => onOpen(c.id)}>
                  <div className={styles.cardTop}>
                    <span className="mono">{c.caseNo}</span>
                    {c.dupFlags?.length > 0 && <AlertTriangle size={13} className={styles.cardFlag} />}
                  </div>
                  <strong className={styles.cardName}>{c.beneficiarySummary?.name || '—'}</strong>
                  <span className={styles.cardVillage}>{c.beneficiarySummary?.village}</span>
                  <div className={styles.cardBottom}>
                    <span className={styles.cat}>
                      {Cat && <Cat size={13} />} {CATEGORY_META[c.category]?.label}
                    </span>
                    <span className="mono">{formatINR(c.amountRequested)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
